import { useEffect, useState } from "react";
import { ScrollReveal } from "@/components/ScrollReveal";
import { api, resolveImage } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CheckCircle2 } from "lucide-react";

export const Doctors = () => {
  const [active, setActive] = useState(null);
  const [doctors, setDoctors] = useState([]);

  useEffect(() => {
    api
      .get("/doctors")
      .then(({ data }) => setDoctors(data))
      .catch(() => setDoctors([]));
  }, []);

  if (doctors.length === 0) return null;

  return (
    <section id="doctors" className="relative bg-bone py-24 lg:py-32 overflow-hidden" data-testid="doctors-section">
      <div className="absolute inset-0 glow-soft pointer-events-none" />
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 relative">
        <ScrollReveal>
          <span className="text-teal text-xs uppercase tracking-[0.3em] font-medium">
            Our Specialists
          </span>
          <h2 className="font-display text-4xl lg:text-5xl text-navy mt-3 max-w-xl">
            Clinical expertise you can actually talk to.
          </h2>
        </ScrollReveal>

        <div
          className={`grid grid-cols-1 sm:grid-cols-2 gap-px bg-navy/15 mt-14 border border-navy/15 ${
            doctors.length > 2 ? "lg:grid-cols-4" : "lg:grid-cols-2 lg:max-w-3xl"
          }`}
        >
          {doctors.map((doc, i) => (
            <ScrollReveal key={doc.id || doc.name} delay={i * 0.08} className="bg-bone">
              <button
                onClick={() => setActive(doc)}
                data-testid={`doctor-card-${i}`}
                className="group card-lift w-full text-left bg-bone hover:bg-white hover:shadow-[0_24px_50px_rgba(15,31,61,0.1)] p-2"
              >
                <div className="overflow-hidden h-72 relative bg-cream flex items-center justify-center">
                  {doc.image && (
                    <img
                      src={resolveImage(doc.image)}
                      alt={doc.name}
                      className="w-full h-full object-contain group-hover:scale-[1.03] transition-transform duration-700"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <span className="absolute bottom-4 left-4 text-[10px] uppercase tracking-[0.25em] text-white translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                    View Profile
                  </span>
                </div>
                <div className="p-4">
                  <h3 className="font-display text-xl text-navy">{doc.name}</h3>
                  <p className="text-teal text-xs uppercase tracking-[0.15em] mt-1">
                    {doc.specialty}
                  </p>
                  <p className="text-muted-foreground text-xs mt-2">
                    {doc.qualification}
                  </p>
                </div>
              </button>
            </ScrollReveal>
          ))}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent
          className="max-w-2xl rounded-none border-navy/10 p-0 overflow-hidden"
          data-testid="doctor-modal"
        >
          {active && (
            <div className="grid sm:grid-cols-[220px_1fr]">
              {active.image && (
                <img
                  src={resolveImage(active.image)}
                  alt={active.name}
                  className="w-full h-full object-contain bg-cream min-h-[240px]"
                />
              )}
              <div className="p-8">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl text-navy">
                    {active.name}
                  </DialogTitle>
                </DialogHeader>
                <p className="text-teal text-xs uppercase tracking-[0.15em] mt-1">
                  {active.specialty}
                </p>
                <p className="text-muted-foreground text-sm mt-1">
                  {active.qualification}
                </p>
                {active.bio && (
                  <p className="text-sm text-navy/80 leading-relaxed mt-4">{active.bio}</p>
                )}
                <ul className="mt-5 space-y-2">
                  {(active.highlights || []).map((h, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-navy/80">
                      <CheckCircle2 size={16} className="text-teal mt-0.5 shrink-0" />
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
